import React, { KeyboardEventHandler, ReactEventHandler } from 'react';
import classNames from 'classnames';

import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { KEY_ENTER } from 'constants/keys';
import { BaseGroupItemPlaceholder, BaseGroupItem } from 'components/Builder/types';
import LineItem from './LineItem';

import styles from './LineView.scss';

interface BuilderLineViewProps {
  collapsed: boolean;
  completed: boolean;
  GroupItemPlaceholder: BaseGroupItemPlaceholder;
  isMobile?: boolean;
  items: (BaseGroupItem | null)[];
  name?: string;
  onSlotClick?: ReactEventHandler;
}

const builderLineViewDefaultPropOnSlotClick = () => {};

const BuilderLineView = ({
  collapsed,
  completed,
  isMobile = false,
  items,
  GroupItemPlaceholder,
  name,
  onSlotClick = builderLineViewDefaultPropOnSlotClick,
}: BuilderLineViewProps) => {
  const mobileMessage = completed ? ', click to change or remove' : `, add a ${name || 'product'}`;
  const accessibleLabel = `${name ? `${name} ` : ''}${completed ? 'completed' : 'incomplete'}${
    isMobile ? `${mobileMessage}` : ''
  }`;

  const handleKeyDown: KeyboardEventHandler<HTMLButtonElement> = event => {
    const { keyCode } = event;

    if (keyCode === KEY_ENTER) {
      onSlotClick(event);
    }
  };

  return (
    <div className={classNames(styles.builderLine)} data-testid="builder-line">
      {name ? (
        <div data-testid="lineTitleWrapper" className={styles.lineTitleWrapper}>
          <div className={styles.line} />
          <Typography
            className={styles.lineTitle}
            element="h3"
            styleAs="sectionHeadingSmall"
            noMargins
            fixedSize
          >
            {name}
          </Typography>
          <div className={styles.line} />
        </div>
      ) : null}
      <ul className={styles.items}>
        {items.map((item, index) => (
          <li
            className={classNames(styles.lineItem, {
              [styles.product]: item,
            })}
          >
            <button
              className={classNames(styles.buttonItem)}
              type="button"
              onClick={onSlotClick}
              onKeyDown={handleKeyDown}
              aria-label={accessibleLabel}
              tabIndex={collapsed ? -1 : 0}
            >
              {item ? (
                <LineItem lineNumber={item.lineNumber} data-testid="builder-line-item" />
              ) : (
                <GroupItemPlaceholder
                  completed={completed}
                  index={index}
                  isMobile={isMobile}
                  items={items}
                />
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

BuilderLineView.displayName = 'BuilderLineView';

export default BuilderLineView;
